import { Component, OnInit, EventEmitter, Output, ViewChild, ElementRef, inject, DestroyRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { MENUCARRIER } from './menu';
import { MenuItem } from './menu.model';
import { MENU } from '../horizontal-topbar/menu';
import { IdentityService, RolesEnum } from 'app/services/authentication/identity.service';
import { MenuService } from 'app/services/menu/menu.services';
import { IMenuUIItem } from 'app/services/menu/menu.dto';
import { debounceTime, distinctUntilChanged, filter, skip } from 'rxjs';
import { Database, ref, objectVal } from '@angular/fire/database';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  menu: any;
  toggle: any = true;
  menuItems: MenuItem[] = [];
  @ViewChild('sideMenu') sideMenu!: ElementRef;
  @Output() mobileMenuButtonClicked = new EventEmitter();

  imageLogo: HTMLImageElement;

  constructor(private router: Router, public translate: TranslateService, private _identityService: IdentityService, private _menuService: MenuService, private db: Database) {
    translate.setDefaultLang('en-us');
    this.imageLogo = new Image();
    this._identityService.imageLogo.subscribe((result) => {
      this.imageLogo.src = result;
      console.log(result)
    });
  }

  ngOnInit(): void {
    // 1) Tomar del cache que cargó APP_INITIALIZER
    this.setMenu(this._menuService.rebuildMenuForCurrentLang());

    // 2) Re-armar labels/iconos cuando cambie el idioma
    this.translate.onLangChange.subscribe(() => {
      this.setMenu(this._menuService.rebuildMenuForCurrentLang());
    });

    // 3) Actualizar item activo en cada navegación
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.initActiveMenu());
  

    // 4) empezar a escuchar el flag RTDB
    this._menuService.startMenuRTDBWatcher(); // o con path custom

    // 5) cuando el servicio refresque, rearmá el menú desde cache
    this._menuService.menuUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        console.log('actualizando menu ')
        this.setMenu(this._menuService.rebuildMenuForCurrentLang());
      });
      
  }  

  ngOnDestroy(): void {
    // Si querés cortar el watcher al salir del sidebar:
    console.log('saliendo del menu ')
    this._menuService.stopMenuRTDBWatcher();
  }

  private subRTDBMenu() {
    // const uid = String(this._identityService.userId);
    const path = `menu_updated`;

    objectVal<boolean | number | null>(ref(this.db, path))
      .pipe( skip(1), debounceTime(250), distinctUntilChanged()/*, etc. */)
      .subscribe(() => {
        console.log('actualizacion menu!!');
      });

  }

  private setMenu(menu: IMenuUIItem[]) {
    // Cambiar el nombre de "Gestión Individuos" a "Gestión Clientes / Customer Management"
    const updatedMenu = this.updateMenuItemNames(menu);
    
    // Clonar y setear estado UI por defecto
    this.menuItems = this.withUiState(updatedMenu);
    // Marcar activo/colapsado según URL actual
    console.log(this.menuItems)
    this.initActiveMenu();
  }

  private updateMenuItemNames(items: IMenuUIItem[]): IMenuUIItem[] {
    return items.map(item => {
      // Si el código es CLI.I, cambiar el nombre usando traducción
      if (item.code === 'CLI.I') {
        const translatedText = this.translate.instant('menu.customers.customer_management');
        const fallbackText = this.translate.currentLang?.startsWith('es') ? 'Gestión Clientes' : 'Customer Management';
        const finalText = translatedText !== 'menu.customers.customer_management' ? translatedText : fallbackText;
        console.log('Translation key result:', translatedText, 'Fallback:', fallbackText, 'Final:', finalText);
        return {
          ...item,
          label: finalText
        };
      }
      
      // Si tiene subItems, aplicar recursivamente y filtrar
      if (item.subItems && item.subItems.length > 0) {
        const filteredSubItems = this.updateMenuItemNames(item.subItems)
          .filter(subItem => subItem.code !== 'CLI.E'); // Ocultar Gestión Empresas
        
        return {
          ...item,
          subItems: filteredSubItems
        };
      }
      
      return item;
    });
  }

  private withUiState(items: IMenuUIItem[]): Array<IMenuUIItem & { isActive?: boolean; isCollapsed?: boolean }> {
    return items.map(i => ({
      ...i,
      isActive: false,
      isCollapsed: true,
      subItems: i.subItems ? this.withUiState(i.subItems) : undefined
    }));
  }

  /**
 * Inicializa la propiedad `isActive` en los subítems del menú según la ruta actual
 * @param subItems Lista de subítems del menú
 * @param currentPath Ruta actual
 */
  private initializeSubItems(subItems: any[], currentPath: string): void {
    subItems.forEach((subItem) => {
      subItem.isActive = this.isMenuItemActive(subItem, currentPath);
      subItem.isCollapsed = !subItem.isActive; // Colapsar subítems no activos
      if (subItem.subItems) {
        this.initializeSubItems(subItem.subItems, currentPath);
        subItem.isCollapsed = !subItem.subItems.some((child: any) => child.isActive); // Abre si hay hijos activos
      }
    });
  }

  /**
   * Verifica si el ítem del menú o alguno de sus subítems coincide con la ruta actual
   * @param item Ítem del menú
   * @param currentPath Ruta actual
   * @returns true si el ítem o alguno de sus subítems está activo
   */
  private isMenuItemActive(item: any, currentPath: string): boolean {
    if (item.link === currentPath) {
      return true;
    }
    if (item.subItems) {
      return item.subItems.some((subItem: any) => this.isMenuItemActive(subItem, currentPath));
    }
    return false;
  }
  
  /***
   * Activate droup down set
   */
  ngAfterViewInit() {
    setTimeout(() => {
      this.initActiveMenu();
    }, 0);
  }

  removeActivation(items: any) {
    items.forEach((item: any) => {
      item.classList.remove("active");
    });
  }

  /** Colapsa recursivamente TODO el árbol */
  private closeAll(items: MenuItem[]): void {
    items.forEach(i => {
      i.isCollapsed = true;
      if (i.subItems) {
        this.closeAll(i.subItems);
      }
    });
  }

  /**
   * Invierte el colapso del ítem clicado,
   * cerrando antes todos los demás padres que no sean su cadena de ancestros.
   */
  toggleItem(item: MenuItem): void {
    if (item) {
      const willOpen = item.isCollapsed;
      // cierra todos los hermanos
      const parent   = this.findParentItem(this.menuItems, item);
      const siblings = parent ? parent.subItems! : this.menuItems;
      siblings.forEach(sib => {
        if (sib !== item) {
          this.closeAll([sib]);
          sib.isCollapsed = true;
        }
      });
      // si vamos a abrir, abre rama de ancestros + él
      if (willOpen) {
        let cur: MenuItem | null = item;
        while (cur) {
          cur.isCollapsed = false;
          cur = this.findParentItem(this.menuItems, cur);
        }
      } else {
        // si vamos a cerrar, ciérralo a él y descendientes
        this.closeAll([item]);
        item.isCollapsed = true;
      }
    }
  }


  /** Encuentra el padre de `child` dentro de `items`, o null */
  private findParentItem(items: MenuItem[], child: MenuItem): MenuItem | null {
    for (const i of items) {
      if (i.subItems?.includes(child)) {
        return i;
      }
      if (i.subItems) {
        const p = this.findParentItem(i.subItems, child);
        if (p) return p;
      }
    }
    return null;
  }

  /** Abre el ítem y todos sus padres */
  private openBranch(item: MenuItem): void {
    let current: MenuItem | null = item;
    while (current) {
      current.isCollapsed = false;
      current = this.findParentItem(this.menuItems, current);
    }
  }

  // toggleItem(item: any): void {
  //   console.log('toggle item: ', item);
  //   this.menuItems.forEach((menuItem: any) => {
  //     if (menuItem === item) {
  //       menuItem.isCollapsed = !menuItem.isCollapsed; // Cambia solo el estado del ítem actual
  //     } else if (!this.isAnyChildActive(menuItem)) {
  //       menuItem.isCollapsed = true; // Cierra los ítems que no tienen hijos activos
  //     }
  
  //     if (menuItem.subItems) {
  //       menuItem.subItems.forEach((subItem: any) => {
  //         if (subItem === item) {
  //           subItem.isCollapsed = !subItem.isCollapsed;
  //         } else if (!this.isAnyChildActive(subItem)) {
  //           subItem.isCollapsed = true;
  //         }
  
  //         console.log(subItem.subItems);
  //         if (subItem.subItems) {
  //           subItem.subItems.forEach((childItem: any) => {
  //             if (childItem === item) {
  //               childItem.isCollapsed = !childItem.isCollapsed;
  //             } else if (!this.isAnyChildActive(childItem)) {
  //               childItem.isCollapsed = false;
  //             }
  //           });
  //         }
  //       });
  //     }
  //   });
  // }  

  private isAnyChildActive(item: any): boolean {
    if (item.isActive) {
      return true;
    }
    if (item.subItems) {
      return item.subItems.some((subItem: any) => this.isAnyChildActive(subItem));
    }
    return false;
  } 

  activateParentDropdown(item: any) {
    // item.classList.add("active");
    let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

    if (parentCollapseDiv) {
      // to set aria expand true remaining
      // parentCollapseDiv.parentElement.children[0].classList.add("active");
      if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
        parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
        if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling)
          parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.classList.add("active");
        if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse")) {
          parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").classList.add("show");
          parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").previousElementSibling.classList.add("active");
        }
      }
      return false;
    }
    return false;
  }

  updateActive(event: MouseEvent, clicked: MenuItem): void {
    event.preventDefault();

    // 1) encuentra padre y siblings
    const parent = this.findParentItem(this.menuItems, clicked);
    const siblings = parent ? parent.subItems! : this.menuItems;

    // 2) cierra todos los hermanos
    siblings.forEach(sib => {
      if (sib !== clicked) {
        this.closeAll([sib]);
        sib.isCollapsed = true;
        sib.isActive    = false;
      }
    });

    // 3) abre la rama del clicado (ancestros + él)
    let cur: MenuItem | null = clicked;
    while (cur) {
      cur.isCollapsed = false;
      cur.isActive    = (cur === clicked);
      cur = this.findParentItem(this.menuItems, cur);
    }
  }

  private resetActiveState(items: any[]): void {
    items.forEach((item) => {
      item.isActive = false; // Limpia el estado activo
      if (item.subItems) {
        this.resetActiveState(item.subItems); // Limpia los subítems
      }
    });
  }  

  // private findParentItem(menuItems: any[], childItem: any): any | null {
  //   for (const menuItem of menuItems) {
  //     if (menuItem.subItems?.includes(childItem)) {
  //       return menuItem;
  //     }

  //     if (menuItem.subItems) {
  //       const parent = this.findParentItem(menuItem.subItems, childItem);
  //       if (parent) {
  //         return parent;
  //       }
  //     }
  //   }

  //   return null;
  // }

  initActiveMenu() {
    let pathName = window.location.pathname;

    const active = this.findMenuItem(pathName, this.menuItems)
    this.toggleItem(active)
    const ul = document.getElementById("navbar-nav");
    if (ul) {
      const items = Array.from(ul.querySelectorAll("a.nav-link"));
      let activeItems = items.filter((x: any) => x.classList.contains("active"));
      this.removeActivation(activeItems);

      let matchingMenuItem = items.find((x: any) => {
          return x.pathname === pathName;
      });
      if (matchingMenuItem) {
        this.activateParentDropdown(matchingMenuItem);
      }
    }

    const currentPath = this.router.url.replace('/', ''); // Obtener la ruta actual

    this.resetActiveState(this.menuItems); // Limpia el estado `isActive`
    console.log('currentPath: ', currentPath);
    const activeItem = this.findMenuItem(currentPath, this.menuItems);
    if (activeItem) {
      activeItem.isActive = true;

      // Marca todos los padres como activos y evita que colapsen
      let parentItem = this.findParentItem(this.menuItems, activeItem);
      while (parentItem) {
        parentItem.isActive = true;
        parentItem.isCollapsed = false; // Evita que se cierren los padres
        parentItem = this.findParentItem(this.menuItems, parentItem);
      }
    }

  }  

  private findMenuItem(pathname: string, menuItems: any[]): any {
    for (const menuItem of menuItems) {
      if (menuItem.link && menuItem.link === pathname) {
        return menuItem;
      }

      if (menuItem.subItems) {
        const foundItem = this.findMenuItem(pathname, menuItem.subItems);
        if (foundItem) {
          return foundItem;
        }
      }
    }
    return null;
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    var sidebarsize = document.documentElement.getAttribute("data-sidebar-size");
    if (sidebarsize == 'sm-hover-active') {
      document.documentElement.setAttribute("data-sidebar-size", 'sm-hover')
    } else {
      document.documentElement.setAttribute("data-sidebar-size", 'sm-hover-active')
    }
  }

  /**
   * SidebarHide modal
   * @param content modal content
   */
  SidebarHide() {
    document.body.classList.remove('vertical-sidebar-enable');
  }

}
